import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { MapPin, Lock, Mail as MailIcon, LogIn } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import api from '../lib/adminApi';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  if (api.isAuthed()) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.login(email, password);
      toast({ title: 'Welcome back', description: 'Signed in successfully.' });
      navigate('/admin');
    } catch (err) {
      toast({ title: 'Login failed', description: err?.response?.data?.detail || 'Check credentials.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b2f5c] via-[#0d3567] to-[#0b2f5c] flex items-center justify-center px-6">
      <div className="absolute top-20 left-1/4 w-96 h-96 bg-sky-400/20 rounded-full blur-3xl" />
      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-300 to-sky-500 flex items-center justify-center shadow-lg">
            <MapPin className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="text-2xl font-bold text-white">Ma<span className="text-sky-300">Life</span> Admin</span>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-8">
          <h1 className="text-2xl font-bold text-[#0b2f5c] mb-1">Sign in</h1>
          <p className="text-sm text-slate-500 mb-6">Access the MaLife control panel.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Email</label>
              <div className="relative">
                <MailIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="email"
                  placeholder="admin@malife.co.bw"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 h-11 focus-visible:ring-sky-400"
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 h-11 focus-visible:ring-sky-400"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#0b2f5c] hover:bg-[#0f3a70] text-white font-semibold rounded-xl mt-2"
            >
              {loading ? 'Signing in…' : <>Sign in <LogIn className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-white/50 mt-6">
          MaLife (Matshelonyana) · Admin Panel
        </p>
      </div>
      <Toaster />
    </div>
  );
};

export default AdminLogin;
